import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { API_BASE_URL } from '@/constants/api';

// Types
interface UserData {
  id: string;
  email: string;
  name?: string;
  lastName?: string;
  role?: string;
  permissions?: string[];
  createdAt?: string;
  updatedAt?: string;
}

interface OrgData {
  id: string;
  name: string;
  plan?: string;
  settings?: Record<string, any>;
  createdAt?: string;
  updatedAt?: string;
}

interface LoginCredentials {
  email: string;
  password: string;
}

interface RegisterData {
  email: string;
  password: string;
  name?: string;
  lastName?: string;
  organizationName?: string;
}

interface AuthResponse {
  authToken: string;
  refreshToken: string;
  userData: UserData;
  orgData: OrgData;
}

// Actual API response structure
interface ApiAuthResponse {
  token: {
    refresh: string;
    access: string;
    user_id: number;
    organization_id: number;
  };
  user_data: {
    pk: number;
    name: string;
    role: string;
    force_change_password: boolean;
    permissions: string[];
    email: string;
  };
  organization: {
    id: number;
    name: string;
    phone: string | null;
    registration_status: string;
    creator: {
      id: number;
      identity: string | null;
      email: string;
      name: string;
      phone: string | null;
      is_active: boolean;
    };
    authorized_signatory: any;
    address: string | null;
    zipcode: string | null;
    created_at: string;
    updated_at: string;
  };
}

interface RefreshTokenResponse {
  authToken: string;
  refreshToken: string;
}

interface AuthState {
  isAuthenticated: boolean;
  userData: UserData | null;
  orgData: OrgData | null;
  isLoading: boolean;
  error: string | null;
}

// Storage keys
const STORAGE_KEYS = {
  AUTH_TOKEN: 'authToken',
  REFRESH_TOKEN: 'refreshToken',
  USER_DATA: 'userData',
  ORG_DATA: 'orgData',
  REMEMBER_ME: 'rememberMe',
} as const;

class AuthService {
  private api: AxiosInstance;
  private refreshPromise: Promise<string> | null = null;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor to include auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = this.getAuthToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Add response interceptor to handle token refresh
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            const newToken = await this.refreshAuthToken();
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return this.api(originalRequest);
          } catch (refreshError) {
            this.logout();
            window.location.href = '/login';
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // Token Management
  setTokens(refreshToken: string, authToken: string): void {
    if (refreshToken) {
      localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
    }
    if (authToken) {
      localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, authToken);
    }
  }

  getAuthToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  }

  clearTokens(): void {
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  }

  // User Data Management
  setUserData(userData: UserData): void {
    if (userData) {
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData));
    }
  }

  getUserData(): UserData | null {
    try {
      const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('Error parsing user data:', error);
      return null;
    }
  }

  clearUserData(): void {
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
  }

  // Organization Data Management
  setOrgData(orgData: OrgData): void {
    if (orgData) {
      localStorage.setItem(STORAGE_KEYS.ORG_DATA, JSON.stringify(orgData));
    }
  }

  getOrgData(): OrgData | null {
    try {
      const orgData = localStorage.getItem(STORAGE_KEYS.ORG_DATA);
      return orgData ? JSON.parse(orgData) : null;
    } catch (error) {
      console.error('Error parsing org data:', error);
      return null;
    }
  }

  clearOrgData(): void {
    localStorage.removeItem(STORAGE_KEYS.ORG_DATA);
  }

  // Authentication State
  isAuthenticated(): boolean {
    const token = this.getAuthToken();
    return !!token && !this.isTokenExpired();
  }

  // Token Validation
  isTokenExpired(): boolean {
    const token = this.getAuthToken();
    if (!token) return true;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      const isExpired = payload.exp < currentTime;
      
      // Debug logging to help identify token expiration issues
      if (process.env.NODE_ENV === 'development') {
        const timeUntilExpiry = payload.exp - currentTime;
        console.log(`Token expires in ${Math.round(timeUntilExpiry / 60)} minutes`);
        if (isExpired) {
          console.warn('Token has expired, should trigger refresh');
        }
      }
      
      return isExpired;
    } catch (error) {
      console.error('Error checking token expiration:', error);
      return true;
    }
  }

  // API Methods
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const response: AxiosResponse<ApiAuthResponse> = await axios.post(`${API_BASE_URL}/auth/login/`, credentials);
      const { token, user_data, organization } = response.data;

      // Transform API response to match our internal structure
      const authToken = token.access;
      const refreshToken = token.refresh;
      
      const userData: UserData = {
        id: user_data.pk.toString(),
        email: user_data.email,
        name: user_data.name.split(' ')[0] || '',
        lastName: user_data.name.split(' ').slice(1).join(' ') || '',
        role: user_data.role,
        permissions: user_data.permissions,
        createdAt: new Date().toISOString(), // API doesn't provide this
        updatedAt: new Date().toISOString(), // API doesn't provide this
      };

      const orgData: OrgData = {
        id: organization.id.toString(),
        name: organization.name,
        plan: 'basic', // API doesn't provide this
        settings: {}, // API doesn't provide this
        createdAt: organization.created_at,
        updatedAt: organization.updated_at,
      };

      this.setTokens(refreshToken, authToken);
      this.setUserData(userData);
      this.setOrgData(orgData);

      // Return transformed data
      return {
        authToken,
        refreshToken,
        userData,
        orgData,
      };
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Login failed');
    }
  }

  async register(data: RegisterData): Promise<AuthResponse> {
    try {
      const response: AxiosResponse<AuthResponse> = await axios.post(`${API_BASE_URL}/auth/register/`, data);
      const { authToken, refreshToken, userData, orgData } = response.data;

      this.setTokens(refreshToken, authToken);
      this.setUserData(userData);
      this.setOrgData(orgData);

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Registration failed');
    }
  }

  async logout(): Promise<void> {
    this.clearAllData();
  }

  async refreshAuthToken(): Promise<string> {
    // Prevent multiple refresh requests
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.performTokenRefresh();
    return this.refreshPromise;
  }

  private async performTokenRefresh(): Promise<string> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      console.log('Attempting to refresh token...');
      const response: AxiosResponse<RefreshTokenResponse> = await this.api.post('/auth/refresh/', {
        refreshToken,
      });

      const { authToken, refreshToken: newRefreshToken } = response.data;
      this.setTokens(newRefreshToken, authToken);
      
      console.log('Token refresh successful');
      return authToken;
    } catch (error: any) {
      console.error('Token refresh failed:', error.response?.data || error.message);
      this.clearAllData();
      throw new Error(error.response?.data?.message || 'Token refresh failed');
    } finally {
      this.refreshPromise = null;
    }
  }

  async validateToken(): Promise<boolean> {
    try {
      await this.api.get('/auth/validate/');
      return true;
    } catch (error) {
      return false;
    }
  }

  async updateProfile(userData: Partial<UserData>): Promise<UserData> {
    try {
      const response: AxiosResponse<UserData> = await this.api.put('/auth/profile/', userData);
      const updatedUserData = response.data;
      this.setUserData(updatedUserData);
      return updatedUserData;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Profile update failed');
    }
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    try {
      await this.api.post('/auth/change-password/', {
        currentPassword,
        newPassword,
      });
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Password change failed');
    }
  }

  async forgotPassword(email: string): Promise<void> {
    try {
      await this.api.post('/auth/forgot-password/', { email });
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Password reset request failed');
    }
  }

  async resetPassword(token: string, newPassword: string): Promise<void> {
    try {
      await this.api.post('/auth/reset-password/', {
        token,
        newPassword,
      });
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Password reset failed');
    }
  }

  // Utility Methods
  getAuthHeader(): Record<string, string> {
    const token = this.getAuthToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  clearAllData(): void {
    this.clearTokens();
    this.clearUserData();
    this.clearOrgData();
    localStorage.removeItem(STORAGE_KEYS.REMEMBER_ME);
  }

  initializeAuth(): AuthState {
    const isAuth = this.isAuthenticated();
    const userData = this.getUserData();
    const orgData = this.getOrgData();

    return {
      isAuthenticated: isAuth,
      userData,
      orgData,
      isLoading: false,
      error: null,
    };
  }

  // Remember Me functionality
  setRememberMe(remember: boolean): void {
    localStorage.setItem(STORAGE_KEYS.REMEMBER_ME, JSON.stringify(remember));
  }

  getRememberMe(): boolean {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEYS.REMEMBER_ME) || 'false');
    } catch {
      return false;
    }
  }

  // Session Management
  getSessionInfo(): {
    isAuthenticated: boolean;
    userData: UserData | null;
    orgData: OrgData | null;
    tokenExpiry: number | null;
    timeUntilExpiry: number | null;
  } {
    const token = this.getAuthToken();
    let tokenExpiry = null;
    let timeUntilExpiry = null;

    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        tokenExpiry = payload.exp;
        timeUntilExpiry = payload.exp - (Date.now() / 1000);
      } catch (error) {
        console.error('Error parsing token payload:', error);
      }
    }

    return {
      isAuthenticated: this.isAuthenticated(),
      userData: this.getUserData(),
      orgData: this.getOrgData(),
      tokenExpiry,
      timeUntilExpiry,
    };
  }

  // Debug method to log current token status
  logTokenStatus(): void {
    if (process.env.NODE_ENV === 'development') {
      const sessionInfo = this.getSessionInfo();
      console.log('=== Token Status ===');
      console.log('Authenticated:', sessionInfo.isAuthenticated);
      console.log('Token Expiry:', sessionInfo.tokenExpiry ? new Date(sessionInfo.tokenExpiry * 1000).toLocaleString() : 'N/A');
      console.log('Time Until Expiry:', sessionInfo.timeUntilExpiry ? `${Math.round(sessionInfo.timeUntilExpiry / 60)} minutes` : 'N/A');
      console.log('Has Refresh Token:', !!this.getRefreshToken());
      console.log('==================');
    }
  }

  // Check if user has specific permission
  hasPermission(permission: string): boolean {
    const userData = this.getUserData();
    return userData?.permissions?.includes(permission) || false;
  }

  // Check if user has specific role
  hasRole(role: string): boolean {
    const userData = this.getUserData();
    return userData?.role === role;
  }

  // Get user's full name
  getUserFullName(): string {
    const userData = this.getUserData();
    if (!userData) return '';
    
    const name = userData.name || '';
    const lastName = userData.lastName || '';
    
    if (name && lastName) {
      return `${name} ${lastName}`;
    } else if (name) {
      return name;
    } else if (lastName) {
      return lastName;
    } else {
      return userData.email;
    }
  }

  // Get user's display name (first name or email)
  getUserDisplayName(): string {
    const userData = this.getUserData();
    if (!userData) return '';
    
    return userData.name || userData.email;
  }
}

// Create and export singleton instance
export const authService = new AuthService();

// Export types for use in other modules
export type { 
  UserData, 
  OrgData, 
  LoginCredentials, 
  RegisterData, 
  AuthResponse, 
  ApiAuthResponse,
  RefreshTokenResponse,
  AuthState 
};

